import React from 'react';
import { observer } from 'mobx-react';

// Translation
import translate from '../../translate/Translate';

// Helpers
import { getUrlSegment } from '../../../helpers';

// Icons
import * as icons from '../../ui/Icons';

// Components
import Appendix from './Appendix';
import Dropdown from '../../common/Dropdown';
import Page from './Page';
import Pagination from '../../common/Pagination';
import ActivityHistory from './ActivityHistory';

const PLGReport = observer(
  class PLGReport extends React.Component {
    constructor() {
      super();

      // Set the initial state
      this.state = {
        activePageIndex: 0,
        attempts: null,
        navigation: []
      };
    }

    componentDidMount() {
      const { AssessmentStore, ReportStore, UserStore } = this.props.store;
      const { targetUserData, userData } = UserStore;
      const { role, userSid } = userData;

      const query = {
        assessmentGroupSid: UserStore.assessmentGroupSid,
        role
      };

      if(role?.toLowerCase() === 'learner') {
        query.userSid = userSid;
        this.getUserAttempts(userSid, 'all-complete');
      } else {
        query.userSid = targetUserData.userSid;
        this.getUserAttempts(targetUserData.userSid, 'all-complete');
      }

      ReportStore.getReportData(query, this.createNavigation);

      if (AssessmentStore.assessments.length === 0) {
        AssessmentStore.listGroupAssessments(UserStore.assessmentGroupSid, {
          userSid: UserStore.userData.userSid
        });
      }
    }

    componentDidUpdate(prevProps) {
      if (prevProps.location.pathname !== this.props.location.pathname) {
        this.setActivePage();
      }
    }

    getUserAttempts = (userSid, completionState) => {
      const { AssessmentStore, UserStore } = this.props.store;

      AssessmentStore.listAttempts({
        userSid,
        completionState,
        assessmentGroupSid: UserStore.assessmentGroupSid
      }, this.formatAttempts);
    }

    formatAttempts = attempts => {
      const attemptObj = {};

      attempts.forEach(attempt => {
        if(!attemptObj[attempt.assessmentSid]) {
          attemptObj[attempt.assessmentSid] = {
            count: 1
          };
        } else {
          attemptObj[attempt.assessmentSid].count += 1;
        }

        if(attempt.mostRecentCompleted) {
          attemptObj[attempt.assessmentSid].lastCompletedDate = attempt.dateCompleted;
        }
      });

      this.setState({
        attempts: attemptObj
      });
    }

    createNavigation = data => {
      const { locale } = this.props.store.AppStore;
      const { role } = this.props.store.UserStore.userData;
      const navigation = [];

      data.forEach(page => {
        const title = page.title[locale];

        navigation.push({
          title,
          url: title.toLowerCase().replace(/ /g, '-')
        });
      });

      if (role === 'administrator' || role === 'instructor') {
        navigation.push({
          title: 'Activity History',
          url: 'activity-history'
        })
      }

      this.setState(
        {
          navigation
        },
        this.setActivePage
      );
    };

    setActivePage = () => {
      let { activePageIndex } = this.state;
      const navigation = [...this.state.navigation];
      const segment = getUrlSegment(2);

      if (segment) {
        for (let i = 0; i < navigation.length; i++) {
          if (navigation[i].url === segment) {
            navigation[i].active = true;
            activePageIndex = i;
          } else {
            navigation[i].active = false;
          }
        }

        this.setState({
          activePageIndex,
          navigation
        });
      }
    };

    resourceClicked = (link, reportSid) => {
      const { ActivityStore, AppStore } = this.props.store;
      const { locale } = AppStore;

      ActivityStore.sendResourceLinkClickedActivity({
        url: link.url,
        label: link.label[locale],
        reportSid
      });
    };

    renderPageType = page => {
      const { attempts } = this.state;
      const { role } = this.props.store.UserStore.userData;

      switch (getUrlSegment(2)) {
        case 'appendix':
          return (
            <Appendix
              {...this.props}
              page={page}
              handleResource={this.resourceClicked}
            />
          );

        case 'activity-history':
          if (role === 'instructor' || role === 'administrator') {
            return (
              <ActivityHistory {...this.props} />
            )
          } else {
            return null;
          }

        default:
          return (
            <Page
              {...this.props}
              attemptCount={attempts[page.assessmentSid]?.count || null}
              handleResource={this.resourceClicked}
              lastCompletedDate={attempts[page.assessmentSid]?.lastCompletedDate || null}
              page={page}
            />
          );
      }
    };

    render() {
      const { activePageIndex, navigation } = this.state;
      const { store, translation } = this.props;
      const { AppStore, ReportStore, UserStore } = store;
      const { reportData } = ReportStore;
      const { signOnData, targetUserData, targetUserId, userData } = UserStore;
      const { lmsId, role } = userData;

      if (!AppStore.loading && reportData) {
        return (
          <React.Fragment>
            <section className="report__head">
              <div className="wrap">
                <article className="report__target" aria-labelledby="learner-info-heading">
                  <h3 id="learner-info-heading" className="meta">{translation.learner_info}</h3>
                  {/* Use target user data for non-learners */}
                  {role.toLowerCase() !== 'learner' && targetUserData &&
                    <div>
                      <p>
                        <strong>{`${targetUserData.firstName} ${targetUserData.lastName}`}</strong>
                      </p>
                      {targetUserId &&
                        <p>
                          <span>Student ID: {targetUserId}</span>
                        </p>
                      }
                    </div>
                  }

                  {/* Use sign on data for learners */}
                  {role.toLowerCase() === 'learner' && signOnData &&
                    <div>
                      <p>
                        <strong>{signOnData.data.lisPersonNameFull}</strong>
                      </p>

                      {lmsId &&
                        <p>
                          <span>Student ID: {lmsId}</span>
                        </p>
                      }
                    </div>
                  }
                </article>

                <nav className="report__nav" aria-labelledby="report-nav">
                  <h2 className="meta" id="report-nav">Report navigation</h2>
                  <Dropdown
                    {...this.props}
                    className="icon--sm report__nav-dropdown"
                    icon={<icons.list />}
                    id="report-navlist"
                    onSelect={activePageIndex => this.setState({ activePageIndex })}
                    options={navigation}
                    triggerText={navigation[activePageIndex]?.title}
                  />
                </nav>

                <Pagination
                  {...this.props}
                  activePageIndex={activePageIndex}
                  className="report__pagination"
                  nextText={translation.pagination.next}
                  nextUrl={navigation[activePageIndex + 1]?.url}
                  onClick={activePageIndex => this.setState({ activePageIndex })}
                  previousText={translation.pagination.previous}
                  previousUrl={navigation[activePageIndex - 1]?.url}
                  title={translation.pagination.title}
                  hideText
                />
              </div>
            </section>

            <main
              id="main-content"
              className="page"
              tabIndex="-1"
              aria-labelledby="page-title"
            >
              {/* Render static or dynamic pages */}
              {this.renderPageType(reportData[activePageIndex])}
            </main>
          </React.Fragment>
        );
      }

      return null;
    }
  }
);

export default translate('PLGReport')(PLGReport);
